import React, { useMemo, useState, useEffect } from 'react';
import { getCsrfToken } from '../../lib/csrf.js';
import { Link, usePage } from '@inertiajs/react';
import Layout from '../../components/Layout.jsx';
import PaymentForm from '../../components/PaymentForm.jsx';
import SeatStepper from '../../components/SeatStepper.jsx';
import ContactSalesModal from '../../components/ContactSalesModal.jsx';
import { ITEM_DEFS, VOLUME_TIERS, VOLUME_DISCOUNT_TIERS, PersonIcon, ShieldIcon, iconFor } from '../Signup/shared.jsx';

function formatPrice(cents, currency = 'usd') {
  if (cents == null) return '$0';
  const amount = (cents / 100).toFixed(2);
  const symbol = currency.toLowerCase() === 'usd' ? '$' : '';
  return `${symbol}${amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
}

const DEFAULT_SEATS = ITEM_DEFS.reduce((acc, d) => ({ ...acc, [d.key]: 0 }), {});

export default function PurchaseLicense({ facility, pricing = {} }) {
  const billingHref = `/facilities/${facility.id}/billing`;
  const { auth } = usePage().props;
  const buyer = auth?.user;

  const [seats, setSeats] = useState(DEFAULT_SEATS);
  const [plan, setPlan] = useState('subscription');
  const [purchaseSession, setPurchaseSession] = useState(null);
  const [purchaseError, setPurchaseError] = useState(null);
  const [purchaseSubmitting, setPurchaseSubmitting] = useState(false);
  const [contactSalesOpen, setContactSalesOpen] = useState(false);

  // Reset state when browser restores page from bfcache
  useEffect(() => {
    const handlePageShow = (e) => {
      if (e.persisted) {
        setSeats(DEFAULT_SEATS);
        setPlan('subscription');
        setPurchaseSession(null);
        setPurchaseError(null);
        setPurchaseSubmitting(false);
      }
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);

  const totalSeats = useMemo(() => Object.values(seats).reduce((a, b) => a + b, 0), [seats]);
  const currentTier = totalSeats >= 20 ? '20+' : totalSeats >= 10 ? '10-19' : totalSeats >= 5 ? '5-9' : null;
  const showContactOption = totalSeats >= 20;

  const availableItems = useMemo(
    () => ITEM_DEFS.filter((d) => !d.hidden && (pricing?.[d.key]?.subscription || pricing?.[d.key]?.one_time)),
    [pricing]
  );
  const caregiverItems = availableItems.filter((d) => d.group === 'caregiver');
  const adminItems = availableItems.filter((d) => d.group === 'admin');

  const computeLineSubtotal = (item) => {
    const price = pricing[item.key];
    const planKey = plan === 'subscription' ? 'subscription' : 'one_time';
    const cents = price?.[planKey]?.unit_cents;
    if (cents == null) return null;
    return cents * seats[item.key];
  };

  const selectedItems = useMemo(() => availableItems.filter((d) => seats[d.key] > 0), [seats, availableItems]);

  const planAvailability = useMemo(() => {
    if (selectedItems.length === 0) return { subscription: false, one_time: false };
    return {
      subscription: selectedItems.every((it) => pricing?.[it.key]?.subscription),
      one_time:     selectedItems.every((it) => pricing?.[it.key]?.one_time),
    };
  }, [selectedItems, pricing]);

  useEffect(() => {
    if (selectedItems.length === 0) return;
    if (plan === 'subscription' && !planAvailability.subscription && planAvailability.one_time) setPlan('one_time');
    else if (plan === 'one_time' && !planAvailability.one_time && planAvailability.subscription) setPlan('subscription');
  }, [plan, planAvailability, selectedItems.length]);
  const orderSubtotal = useMemo(() => selectedItems.reduce((sum, item) => sum + (computeLineSubtotal(item) || 0), 0), [selectedItems, seats, plan, pricing]);
  const orderDiscount = useMemo(() => {
    const tier = VOLUME_DISCOUNT_TIERS.find((t) => totalSeats >= t.min);
    if (!tier) return 0;
    return Math.round((orderSubtotal * tier.pct) / 100);
  }, [orderSubtotal, totalSeats]);
  const orderTotal = Math.max(0, orderSubtotal - orderDiscount);

  const handleContinueToPayment = async () => {
    if (totalSeats <= 0 || purchaseSubmitting) return;
    setPurchaseSubmitting(true);
    setPurchaseError(null);

    const items = ITEM_DEFS.filter((d) => seats[d.key] > 0).map((d) => ({
      role: d.role, training_type: d.training_type, quantity: seats[d.key],
    }));

    const csrfToken = getCsrfToken();
    try {
      const response = await fetch(`/facilities/${facility.id}/billing/purchase_bundle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
        body: JSON.stringify({ plan, items }),
      });
      const data = await response.json();
      if (response.ok && data.client_secret) {
        setPurchaseSession({
          clientSecret:   data.client_secret,
          publishableKey: data.publishable_key,
          returnUrl:      data.return_url,
          mode:           data.mode,
          amountCents:    data.amount_cents,
        });
      } else {
        setPurchaseError(data.error || 'Could not start checkout.');
      }
    } catch {
      setPurchaseError('Network error. Please try again.');
    } finally {
      setPurchaseSubmitting(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto p-6 sm:p-8 space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900">
            {purchaseSession ? 'Complete payment' : 'Purchase Licenses'}
          </h1>
          <p className="text-sm text-gray-500 mt-1">{facility.name}</p>
        </div>

        {purchaseError && (
          <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-800">{purchaseError}</div>
        )}

        {purchaseSession ? (
          <div className="bg-white border border-gray-300 rounded-lg p-6 space-y-5">
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <div className="divide-y divide-gray-200">
                {selectedItems.map((item) => (
                  <div key={item.key} className="py-2 flex justify-between text-sm">
                    <div>
                      <p className="font-medium text-gray-900">{item.role} {item.label}</p>
                      <p className="text-xs text-gray-500">{seats[item.key]} {seats[item.key] === 1 ? 'seat' : 'seats'}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="pt-2 border-t border-gray-200 mt-2 flex justify-between items-center">
                <span className="text-sm font-semibold text-gray-900">{purchaseSession.mode === 'subscription' ? 'Due today' : 'Total'}</span>
                <span className="text-lg font-bold text-indigo-600">{formatPrice(purchaseSession.amountCents)}</span>
              </div>
            </div>

            <PaymentForm
              clientSecret={purchaseSession.clientSecret}
              publishableKey={purchaseSession.publishableKey}
              returnUrl={purchaseSession.returnUrl}
              submitLabel={`Pay ${formatPrice(purchaseSession.amountCents)}`}
            />

            <button
              type="button"
              onClick={() => setPurchaseSession(null)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 cursor-pointer"
            >
              Edit selection
            </button>
          </div>
        ) : (
          <div className="bg-white border border-gray-300 rounded-lg p-6 space-y-6">
            <div className="rounded-md bg-indigo-50 border border-indigo-100 p-3 flex items-start gap-3">
              <ShieldIcon className="h-5 w-5 text-indigo-500 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-xs font-medium text-indigo-900">Volume discounts apply automatically based on total seats selected.</p>
                <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
                  {VOLUME_TIERS.map((tier) => (
                    <div key={tier.label}>
                      <p className="font-medium text-gray-700">{tier.range}</p>
                      {tier.label === '20+' ? (
                        <button type="button" onClick={() => setContactSalesOpen(true)} className="font-semibold text-indigo-600 hover:text-indigo-700 underline underline-offset-2 cursor-pointer">Contact us</button>
                      ) : (
                        <p className={currentTier === tier.label ? 'font-bold text-green-700' : 'font-semibold text-gray-500'}>{tier.pct}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {caregiverItems.length > 0 && (
              <div>
                <h4 className="flex items-center gap-2 font-semibold text-gray-900 mb-3"><PersonIcon /> Caregiver / Direct Care Staff</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {caregiverItems.map((d) => (
                    <SeatStepper key={d.key} icon={iconFor[d.key]} label={d.label} description={d.hours}
                      value={seats[d.key]} onChange={(v) => setSeats({ ...seats, [d.key]: v })} />
                  ))}
                </div>
              </div>
            )}

            {adminItems.length > 0 && (
              <div>
                <h4 className="flex items-center gap-2 font-semibold text-gray-900 mb-3"><PersonIcon /> Facility Administration</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {adminItems.map((d) => (
                    <SeatStepper key={d.key} icon={iconFor[d.key]} label={d.label} description={d.hours}
                      value={seats[d.key]} onChange={(v) => setSeats({ ...seats, [d.key]: v })} />
                  ))}
                </div>
              </div>
            )}

            {availableItems.length === 0 && (
              <div className="rounded-md bg-yellow-50 border border-yellow-200 p-4 text-sm text-yellow-900">
                No license bundles are currently available for your facility type. Please contact support.
              </div>
            )}

            {showContactOption && (
              <div className="rounded-md bg-indigo-50 border border-indigo-100 p-3 text-sm text-indigo-900">
                Buying 20 or more seats? You can continue with self-serve checkout, or{' '}
                <button type="button" onClick={() => setContactSalesOpen(true)} className="font-semibold underline hover:text-indigo-700 cursor-pointer">contact sales</button>{' '}
                for custom pricing.
              </div>
            )}

            <div className="space-y-3">
              <p className="text-sm font-semibold text-gray-800">Choose your payment option</p>
              {planAvailability.subscription && (
                <label className={`block cursor-pointer rounded-lg border-2 p-4 transition-colors ${plan === 'subscription' ? 'border-indigo-600 bg-indigo-50/40' : 'border-gray-200 hover:border-indigo-300'}`}>
                  <div className="flex items-start gap-3">
                    <input type="radio" name="plan" checked={plan === 'subscription'} onChange={() => setPlan('subscription')} className="mt-1 h-4 w-4 text-indigo-600" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-gray-900">Team Subscription – Full Access</h3>
                        <span className="inline-flex items-center rounded-md bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">Best Value</span>
                      </div>
                      <p className="mt-1 text-sm text-gray-600">Keep your team compliant year-round with automatic renewal and uninterrupted training access.</p>
                    </div>
                  </div>
                </label>
              )}
              {planAvailability.one_time && (
                <label className={`block cursor-pointer rounded-lg border-2 p-4 transition-colors ${plan === 'one_time' ? 'border-indigo-600 bg-indigo-50/40' : 'border-gray-200 hover:border-indigo-300'}`}>
                  <div className="flex items-start gap-3">
                    <input type="radio" name="plan" checked={plan === 'one_time'} onChange={() => setPlan('one_time')} className="mt-1 h-4 w-4 text-indigo-600" />
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900">One-Time Team Bundle</h3>
                      <p className="mt-1 text-sm text-gray-600">Pay once for access to complete this training within the current cycle.</p>
                    </div>
                  </div>
                </label>
              )}
              {totalSeats > 0 && !planAvailability.subscription && !planAvailability.one_time && (
                <div className="rounded-md bg-yellow-50 border border-yellow-200 p-3 text-sm text-yellow-900">
                  No common payment option is available for your current seat mix. Adjust your selection to continue.
                </div>
              )}
              <div className="rounded-md bg-gray-50 border border-gray-200 p-4 space-y-2">
                <div className="flex justify-between text-sm text-gray-700">
                  <span>Subtotal</span><span>{formatPrice(orderSubtotal)}</span>
                </div>
                {orderDiscount > 0 && (
                  <div className="flex justify-between text-sm font-medium text-green-700">
                    <span>Volume discount</span><span>−{formatPrice(orderDiscount)}</span>
                  </div>
                )}
                <div className="pt-2 border-t border-gray-200 flex justify-between items-center">
                  <span className="font-semibold text-gray-900">Total</span>
                  <span className="text-xl font-bold text-indigo-600">{formatPrice(orderTotal)}</span>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Link href={billingHref}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 cursor-pointer">
                Cancel
              </Link>
              <button type="button" onClick={handleContinueToPayment} disabled={totalSeats === 0 || purchaseSubmitting || (!planAvailability.subscription && !planAvailability.one_time)}
                className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">
                {purchaseSubmitting ? 'Preparing checkout…' : 'Continue to payment'}
              </button>
            </div>
          </div>
        )}
      </div>

      <ContactSalesModal
        open={contactSalesOpen}
        onClose={() => setContactSalesOpen(false)}
        buyer={buyer}
        facility={facility}
        totalSeats={totalSeats}
        seatsBreakdown={seats}
      />
    </Layout>
  );
}
